import TonIcon from "@/assets/icons/ton-icon.svg?react"
import GiftIcon from "@/assets/icons/gift.svg?react"

import { cn } from "@/shared/lib/utils"

export type BetAsset = "TON" | "GIFT"

interface BetAssetToggleProps {
  value: BetAsset
  onChange: (value: BetAsset) => void
  className?: string
  /**
   * Optional aria labels per option (i18n strings). Defaults match
   * common usage in the betting panel.
   */
  ariaLabels?: { ton: string; gift: string }
}

const activeIconClass =
  "text-[#00FE74] [filter:drop-shadow(0_0_4px_rgba(0,254,116,0.6))]"
const inactiveIconClass = "text-white/25"

/**
 * Two-state asset switcher above the betting panel (24px icons, `p-1.5` track).
 * The whole pill is one tap target — each press toggles TON ↔ GIFT.
 */
export function BetAssetToggle({
  value,
  onChange,
  className,
  ariaLabels,
}: BetAssetToggleProps) {
  const tonLabel = ariaLabels?.ton ?? "TON"
  const giftLabel = ariaLabels?.gift ?? "Gifts"
  const currentLabel = value === "TON" ? tonLabel : giftLabel

  const handleToggle = () => {
    onChange(value === "TON" ? "GIFT" : "TON")
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={value === "GIFT"}
      aria-label={currentLabel}
      onClick={handleToggle}
      className={cn(
        "inline-flex items-center gap-2 rounded-full bg-secondary-main p-1.5",
        "transition-opacity active:opacity-90 focus:outline-none",
        className
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-6 items-center justify-center transition-colors",
          value === "TON" ? activeIconClass : inactiveIconClass
        )}
      >
        <TonIcon className="size-6" />
      </span>
      <span
        aria-hidden
        className={cn(
          "flex size-6 items-center justify-center transition-colors",
          value === "GIFT" ? activeIconClass : inactiveIconClass
        )}
      >
        <GiftIcon className="size-6" />
      </span>
    </button>
  )
}
